import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AccountMembership } from 'app/API.service.custom';
import { accessKeyFromSnapshot, decideAccess, isSafeRedirectTarget } from './route-access-policy';

/**
 * Layer B of route access hardening: re-checks the CURRENTLY committed route
 * when the active account becomes available (cold deep-link) or changes
 * (account switch). The nav-time boundary is the AccessGuard; this is the net
 * for the case where the guard passed through because the account was not yet
 * loaded.
 *
 * Replaces the duplicated NavigationStart redirect blocks that previously lived
 * in both DefaultLayoutComponent and AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class RouteAccessEnforcer {
  constructor(private router: Router, private toastr: ToastrService) {}

  /**
   * Enforce policy for the current route given the (now known) account.
   * Idempotent and loop-safe: no-op when allowed, when account is unknown, or
   * when already sitting on a safe landing zone / the redirect target.
   */
  enforceCurrentRoute(account: AccountMembership): void {
    if (!account) return;

    const snapshot = this.router.routerState.snapshot;
    const key = accessKeyFromSnapshot(snapshot);
    if (!key) return;

    const decision = decideAccess(key, account);
    if (decision.allowed) return;

    const currentUrl = snapshot.url || this.router.url;
    const target = decision.redirectTo || '/home/dashboard';
    const currentPath = currentUrl.split('?')[0];
    const targetPath = target.split('?')[0];

    // Loop-safety: never bounce away from a safe zone or the target itself.
    if (isSafeRedirectTarget(currentUrl) || currentPath === targetPath) return;

    if (decision.message) this.toastr.error(decision.message);
    // Update the URL to the denial target, matching AccessGuard's UrlTree behavior
    // (the old blocks used skipLocationChange, which left the forbidden URL showing).
    this.router.navigateByUrl(target);
  }
}
